import { useEffect, useRef } from 'react';
import { patchSessionTranscript } from '@/lib/api-client';
import type { SpeakerRoleMap, Turn } from '@/lib/types';

const SAVE_INTERVAL_MS = 5_000;

interface Options {
  sessionId: string | null;
  transcript: string;
  /** P6 S1: speaker turns + roles synced alongside the flat transcript. */
  turns?: Turn[];
  speakerRoles?: SpeakerRoleMap;
  enabled?: boolean;
}

/**
 * Periodically PATCHes the session's transcript to the server while listening.
 *
 * Runs at most once per SAVE_INTERVAL_MS, only when the transcript has
 * actually changed since the last save. Also flushes on unmount and on
 * sessionId change so the operator does not lose the trailing buffer when
 * switching matters mid-hearing.
 */
export function useTranscriptSync({
  sessionId,
  transcript,
  turns,
  speakerRoles,
  enabled = true,
}: Options): void {
  const transcriptRef = useRef(transcript);
  const turnsRef = useRef<Turn[] | undefined>(turns);
  const rolesRef = useRef<SpeakerRoleMap | undefined>(speakerRoles);
  const lastSavedRef = useRef<string>('');
  const timerRef = useRef<number | null>(null);
  const sessionRef = useRef<string | null>(sessionId);

  // Keep refs current so the timer callback can read latest values without
  // being recreated on every render.
  transcriptRef.current = transcript;
  turnsRef.current = turns;
  rolesRef.current = speakerRoles;

  useEffect(() => {
    if (!enabled || !sessionId) {
      return;
    }

    if (sessionRef.current !== sessionId) {
      // Switched session. Reset the lastSaved baseline so the new session
      // gets its first save written even if the text happens to match.
      lastSavedRef.current = '';
      sessionRef.current = sessionId;
    }

    function flush(): void {
      if (!sessionId) {
        return;
      }
      const next = transcriptRef.current;
      if (next === lastSavedRef.current) {
        return;
      }
      const snapshot = next;
      patchSessionTranscript(sessionId, snapshot, {
        ...(turnsRef.current !== undefined && { turns: turnsRef.current }),
        ...(rolesRef.current !== undefined && { speakerRoles: rolesRef.current }),
      })
        .then(() => {
          lastSavedRef.current = snapshot;
        })
        .catch((err: unknown) => {
          // Swallow: the next interval will retry. Surface to console for dev.
          console.warn('transcript save failed:', err);
        });
    }

    timerRef.current = window.setInterval(flush, SAVE_INTERVAL_MS);
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Final flush on unmount or session change.
      flush();
    };
  }, [enabled, sessionId]);
}
